import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
// Public / error entry pages stay eager — they are the first paint and tiny,
// and the admin-chrome Suspense skeleton would look wrong on the login screen.
import Login from '../pages/auth/Login';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { PERMISSIONS } from '@/lib/permissions';

// Heavy authenticated pages are code-split (Suspense fallback lives in App.tsx).
const SetupWizardShell = lazy(() => import('../components/setup-wizard/SetupWizardShell'));
const Onboarding2Page = lazy(() => import('../pages/onboarding/Onboarding2Page'));
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
// W1.2 — 2FA challenge/enrollment screens.
const TwoFactorChallengePage = lazy(() => import('../pages/auth/TwoFactorChallengePage'));
const TwoFactorEnrollPage = lazy(() => import('../pages/auth/TwoFactorEnrollPage'));
// NotFoundPage statically imports AdminLayout (sidebar/header/command-palette
// chrome) for authenticated visitors — pulling that into the eager entry
// bundle just for a catch-all 404 route bloats every login/portal page load.
// It's an edge case, not the first paint, so it is code-split like the other
// heavy authenticated pages above.
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

/**
 * Auth-related routes: login, forbidden, setup, onboarding, dashboard, and root redirects.
 */
export const authRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/admin/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/forbidden',
    element: <ForbiddenPage />,
  },
  {
    path: '/2fa/challenge',
    element: (
      <ProtectedRoute>
        <TwoFactorChallengePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/2fa/enroll',
    element: (
      <ProtectedRoute>
        <TwoFactorEnrollPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/setup',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <SetupWizardShell />
      </ProtectedRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <Onboarding2Page />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    // Alias — admin sidebar links sometimes use the /admin/* prefix pattern
    path: '/admin/dashboard',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  // BUG-E39: replace silent redirect-to-dashboard with explicit 404 page.
  // Unknown /admin/* paths (and any other unmatched URL) now render NotFoundPage.
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
